'use client';

import type { OptimizelyUserInfo } from 'providers/OptimizelyProvider';
import {
  createContext,
  type Dispatch,
  type ReactNode,
  type SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { CookieService } from '@/app_lib/CookieService';
import {
  getCurrentUserClassification,
  pushToDataLayer,
  syncTrackedUserClassification,
  trackUserStateChange,
} from '@/app_lib/services/Gtm/utils';
import {
  findOnetrustCookieOptIn,
  findOnetrustCookieOptInAds,
  findOnetrustCookiePerformance,
} from '@/app_lib/services/oneTrust/cookies';
import { getUserAccessClient } from '@/app_lib/services/tinypass/clientHelpers';
import type { UserClassification } from '@/app_lib/services/userClassification';
import type { UserAccess } from '@/app_lib/types/AuthAccess';
import type { PianoPageConfig } from '@/app_lib/types/accounts/piano/Piano';
import { useOptimizely } from '@/main/lib/hooks/useOptimizely';

interface UserContextInterface {
  hasConsent: boolean | null;
  isMobileApp: boolean;
  isUserAccessResolved: boolean;
  performanceTracking: boolean | null;
  personalizedAds: boolean | null;
  pianoLoadingState: 'loading' | 'loaded' | 'failed';
  pianoPageConfig?: PianoPageConfig;
  refreshUserAccess: () => Promise<void>;
  setHasConsent: (consent: boolean | null) => void;
  setPerformanceTracking: (value: boolean | null) => void;
  setPersonalizedAds: (value: boolean | null) => void;
  setPianoLoadingState: (state: 'loading' | 'loaded' | 'failed') => void;
  setPianoPageConfig: (config: PianoPageConfig) => void;
  setStopVideoDock: Dispatch<SetStateAction<boolean>>;
  setUserAccess: (access: UserAccess) => void;
  stopVideoDock: boolean;
  userAccess: UserAccess;
  userVariants: OptimizelyUserInfo | null;
}

const noop = () => {
  // noop default setters for context initialization
};
const asyncNoop = async () => {
  // noop default for async context methods
};

const defaultUserContext: UserContextInterface = {
  hasConsent: null,
  isMobileApp: false,
  isUserAccessResolved: false,
  performanceTracking: null,
  personalizedAds: null,
  pianoLoadingState: 'loading',
  pianoPageConfig: undefined,
  refreshUserAccess: asyncNoop,
  setHasConsent: noop,
  setPerformanceTracking: noop,
  setPersonalizedAds: noop,
  setPianoLoadingState: noop,
  setPianoPageConfig: noop,
  setStopVideoDock: noop,
  setUserAccess: noop,
  stopVideoDock: false,
  userAccess: { data: null, hasAccess: false, isPaused: false, loggedIn: undefined, pianoUserToken: '' },
  userVariants: null,
};

interface UserProviderProps {
  children: ReactNode;
}

function getInitialUserAccess(): UserAccess {
  const pianoUid = CookieService.get('pianoUid');
  if (pianoUid) {
    const pianoAccess = CookieService.get('pianoAccess');
    return {
      data: null,
      hasAccess: pianoAccess === 'true',
      isPaused: CookieService.get('pianoPaused') === 'true',
      loggedIn: true,
      pianoUserToken: '',
    };
  }
  return defaultUserContext.userAccess;
}

// biome-ignore lint/style/useComponentExportOnlyModules: context should be with provider
export const UserContext = createContext<UserContextInterface>(defaultUserContext);

export const UserProvider = ({ children }: UserProviderProps) => {
  const [hasConsent, setHasConsent] = useState<boolean | null>(findOnetrustCookieOptIn());
  const [personalizedAds, setPersonalizedAds] = useState<boolean | null>(findOnetrustCookieOptInAds());
  const [performanceTracking, setPerformanceTracking] = useState<boolean | null>(findOnetrustCookiePerformance());
  const [pianoLoadingState, setPianoLoadingState] = useState<'loading' | 'loaded' | 'failed'>(
    defaultUserContext.pianoLoadingState,
  );
  const [pianoPageConfig, setPianoPageConfig] = useState<PianoPageConfig | undefined>(
    defaultUserContext.pianoPageConfig,
  );
  const [isMobileApp, setIsMobileApp] = useState(false);
  const [stopVideoDock, setStopVideoDock] = useState(false);
  const [userAccess, setUserAccess] = useState<UserAccess>(getInitialUserAccess);
  const previousClassificationRef = useRef<UserClassification | null>(null);
  const previousLoggedInRef = useRef<boolean | undefined>(undefined);
  const { userFlags } = useOptimizely();

  useEffect(() => {
    let isMounted = true;

    async function getUserAccess() {
      const userAccessClient = await getUserAccessClient();
      if (isMounted) {
        setUserAccess(userAccessClient);
      }
    }
    getUserAccess();
    return () => {
      isMounted = false;
    };
  }, []);

  const refreshUserAccess = useCallback(async () => {
    const userAccessClient = await getUserAccessClient();
    setUserAccess(userAccessClient);
  }, []);

  useEffect(() => {
    const isWkWebView =
      navigator.userAgent.includes('iPhone') &&
      !navigator.userAgent.includes('Version/') &&
      !navigator.userAgent.includes('Safari/');
    if (isWkWebView) {
      setIsMobileApp(true);
    }
  }, []);

  useEffect(() => {
    if (userAccess.loggedIn === undefined) {
      return;
    }

    // biome-ignore lint/style/useNamingConvention: <external data>
    pushToDataLayer({ loginStatusCD: userAccess.loggedIn ? 'logged-in' : 'guest' });

    const currentClassification = getCurrentUserClassification();

    if (previousLoggedInRef.current === undefined) {
      syncTrackedUserClassification(currentClassification);
    } else if (previousLoggedInRef.current !== userAccess.loggedIn) {
      trackUserStateChange({
        nextClassification: currentClassification,
        previousClassification: previousClassificationRef.current ?? undefined,
      });
    } else {
      syncTrackedUserClassification(currentClassification);
    }

    previousClassificationRef.current = currentClassification;
    previousLoggedInRef.current = userAccess.loggedIn;
  }, [userAccess.loggedIn]);

  const isUserAccessResolved = userAccess.loggedIn !== undefined;

  const userState = useMemo(
    () => ({
      hasConsent,
      isMobileApp,
      isUserAccessResolved,
      performanceTracking,
      personalizedAds,
      pianoLoadingState,
      pianoPageConfig,
      refreshUserAccess,
      setHasConsent,
      setPerformanceTracking,
      setPersonalizedAds,
      setPianoLoadingState,
      setPianoPageConfig,
      setStopVideoDock,
      setUserAccess,
      stopVideoDock,
      userAccess,
      userVariants: userFlags,
    }),
    [
      isMobileApp,
      isUserAccessResolved,
      pianoLoadingState,
      pianoPageConfig,
      hasConsent,
      personalizedAds,
      refreshUserAccess,
      stopVideoDock,
      performanceTracking,
      userFlags,
      userAccess,
    ],
  );

  return <UserContext.Provider value={userState}>{children}</UserContext.Provider>;
};

// biome-ignore lint/style/useComponentExportOnlyModules: context should be with provider
export const useUser = () => {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
};
